import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import * as XLSX from 'xlsx';
import { LibraryModule } from '../shared/lib/lib.module';
import { NiTableComponent } from '../shared/lib/ni-table/ni-table.component';
import { NiTableCellDirective } from '../shared/lib/ni-table/ni-table-cell.directive';
import { NiTableColumn } from '../shared/lib/ni-table/ni-table.models';
import {
  MealMenuAdminService,
  MealMenuBulkRowError,
  MealMenuBulkUpdateItem,
  MealMenuItem,
} from '../core/services/meal-menu-admin.service';

export type MealMenuListRow = {
  id: string;
  day: string;
  vegHotMeal: string;
  vegColdMeal: string;
  nonVegHotMeal: string;
  nonVegColdMeal: string;
};

@Component({
  selector: 'meal-menu-management-component',
  standalone: true,
  templateUrl: './meal-menu-management.component.html',
  styleUrls: ['./meal-menu-management.component.css'],
  imports: [
    CommonModule,
    FormsModule,
    RouterModule,
    LibraryModule,
    NiTableComponent,
    NiTableCellDirective,
  ],
})
export class MealMenuManagementComponent implements OnInit {
  @ViewChild('bulkFileInput') bulkFileInput?: ElementRef<HTMLInputElement>;

  isInitialLoading = true;
  isEditOpen = false;
  isBulkUploadOpen = false;
  isBulkUploading = false;
  searchText = '';
  errorMessage: string | null = null;

  mealList: MealMenuListRow[] = [];
  filteredList: MealMenuListRow[] = [];
  columns: NiTableColumn<MealMenuListRow>[] = [];
  editingRow: MealMenuListRow | null = null;

  editVegHotMeal = '';
  editVegColdMeal = '';
  editNonVegHotMeal = '';
  editNonVegColdMeal = '';
  isSavingEdit = false;

  bulkSelectedFile: File | null = null;
  bulkSelectedFileName = '';
  bulkErrorMessage: string | null = null;
  bulkSuccessMessage: string | null = null;
  bulkRowErrors: MealMenuBulkRowError[] = [];

  /** Bulk template: Date + veg/non-veg hot & cold columns. */
  private readonly bulkTemplateHeaders = [
    'Date',
    'VegHotMeal',
    'VegColdMeal',
    'NonVegHotMeal',
    'NonVegColdMeal',
  ] as const;

  /** Centers are closed weekends — weekly meal menu is Monday–Friday only. */
  private readonly weekdayNames = new Set([
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
  ]);

  constructor(
    private readonly router: Router,
    private readonly mealMenuAdminService: MealMenuAdminService,
  ) {}

  ngOnInit(): void {
    const token = sessionStorage.getItem('token');
    if (!token) {
      void this.router.navigate(['/'], { replaceUrl: true });
      return;
    }

    this.columns = [
      { key: 'day', label: 'Day', custom: true },
      { key: 'vegHotMeal', label: 'Veg Hot Meal', custom: true },
      { key: 'vegColdMeal', label: 'Veg Cold Meal', custom: true },
      { key: 'nonVegHotMeal', label: 'Non-Veg Hot Meal', custom: true },
      { key: 'nonVegColdMeal', label: 'Non-Veg Cold Meal', custom: true },
      { key: 'action', label: 'Action', custom: true, align: 'right' },
    ];

    this.loadMenus();
  }

  goBack(): void {
    void this.router.navigate(['/manage-services/senior-center']);
  }

  onSearchInput(value: string): void {
    this.searchText = value ?? '';
    this.applySearch();
  }

  applySearch(): void {
    const query = this.searchText.trim().toLowerCase();
    this.filteredList = this.mealList.filter((row) => {
      if (!query) {
        return true;
      }
      return (
        row.day.toLowerCase().includes(query) ||
        row.vegHotMeal.toLowerCase().includes(query) ||
        row.vegColdMeal.toLowerCase().includes(query) ||
        row.nonVegHotMeal.toLowerCase().includes(query) ||
        row.nonVegColdMeal.toLowerCase().includes(query)
      );
    });
  }

  onDownload(): void {
    const today = new Date();
    const rows = this.filteredList.map((row, index) => {
      const sampleDate = this.sampleDateForWeekday(row.day, today, index);
      return {
        Date: sampleDate,
        VegHotMeal: row.vegHotMeal === '—' ? '' : row.vegHotMeal,
        VegColdMeal: row.vegColdMeal === '—' ? '' : row.vegColdMeal,
        NonVegHotMeal: row.nonVegHotMeal === '—' ? '' : row.nonVegHotMeal,
        NonVegColdMeal: row.nonVegColdMeal === '—' ? '' : row.nonVegColdMeal,
      };
    });
    const worksheet = XLSX.utils.json_to_sheet(rows, {
      header: [...this.bulkTemplateHeaders],
    });
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Meal Menu');
    XLSX.writeFile(workbook, 'meal-menu.xlsx');
  }

  openBulkUploadModal(): void {
    this.isBulkUploadOpen = true;
    this.resetBulkUploadState();
  }

  closeBulkUploadModal(): void {
    if (this.isBulkUploading) {
      return;
    }
    this.isBulkUploadOpen = false;
    this.resetBulkUploadState();
  }

  downloadBulkTemplate(): void {
    // Row 2 shows the expected Date format (MM/DD/YYYY) as a fill-in example.
    const worksheet = XLSX.utils.aoa_to_sheet([
      [...this.bulkTemplateHeaders],
      ['MM/DD/YYYY', '', '', '', ''],
    ]);
    worksheet['!cols'] = [{ wch: 14 }, { wch: 28 }, { wch: 28 }, { wch: 28 }, { wch: 28 }];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Meal Menu');
    XLSX.writeFile(workbook, 'meal-menu-bulk-upload-template.xlsx');
  }

  triggerBulkFilePicker(): void {
    this.bulkFileInput?.nativeElement.click();
  }

  onBulkFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0] ?? null;
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];

    if (!file) {
      this.bulkSelectedFile = null;
      this.bulkSelectedFileName = '';
      return;
    }

    if (!this.isSupportedBulkFile(file.name)) {
      this.bulkSelectedFile = null;
      this.bulkSelectedFileName = '';
      this.bulkErrorMessage = 'Please upload a CSV or Excel file (.csv, .xlsx, .xls).';
      input.value = '';
      return;
    }

    this.bulkSelectedFile = file;
    this.bulkSelectedFileName = file.name;
  }

  submitBulkUpload(): void {
    if (!this.bulkSelectedFile || this.isBulkUploading) {
      return;
    }

    this.isBulkUploading = true;
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];

    const file = this.bulkSelectedFile;
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const menus = this.parseBulkFile(file, reader.result);
        if (!menus.length) {
          this.isBulkUploading = false;
          this.bulkErrorMessage = 'No meal menu rows found in the file.';
          return;
        }

        this.mealMenuAdminService.bulkUpdateMenus(menus).subscribe({
          next: (response) => {
            this.isBulkUploading = false;
            this.bulkRowErrors = response.errors ?? [];
            this.bulkSuccessMessage =
              response.message ||
              `Updated ${response.updated ?? 0} of ${response.total ?? menus.length} days.`;
            if ((response.updated ?? 0) > 0) {
              this.loadMenus({ silent: true });
            }
            if ((response.failed ?? 0) === 0) {
              setTimeout(() => this.closeBulkUploadModal(), 700);
            }
          },
          error: (err) => {
            this.isBulkUploading = false;
            this.bulkErrorMessage =
              err?.error?.message ||
              err?.error?.Message ||
              'Unable to upload meal menu. Please try again.';
          },
        });
      } catch (error) {
        this.isBulkUploading = false;
        this.bulkErrorMessage =
          error instanceof Error ? error.message : 'Unable to read the uploaded file.';
      }
    };
    reader.onerror = () => {
      this.isBulkUploading = false;
      this.bulkErrorMessage = 'Unable to read the selected file.';
    };

    if (this.isExcelFile(file.name)) {
      reader.readAsArrayBuffer(file);
    } else {
      reader.readAsText(file);
    }
  }

  openEdit(row: MealMenuListRow): void {
    this.editingRow = row;
    this.editVegHotMeal = row.vegHotMeal === '—' ? '' : row.vegHotMeal;
    this.editVegColdMeal = row.vegColdMeal === '—' ? '' : row.vegColdMeal;
    this.editNonVegHotMeal = row.nonVegHotMeal === '—' ? '' : row.nonVegHotMeal;
    this.editNonVegColdMeal = row.nonVegColdMeal === '—' ? '' : row.nonVegColdMeal;
    this.isEditOpen = true;
  }

  closeEdit(): void {
    if (this.isSavingEdit) {
      return;
    }
    this.isEditOpen = false;
    this.editingRow = null;
  }

  saveEdit(): void {
    if (!this.editingRow || this.isSavingEdit) {
      return;
    }

    this.isSavingEdit = true;
    this.mealMenuAdminService
      .updateMenu(this.editingRow.day, {
        vegHotMeal: this.editVegHotMeal.trim(),
        vegColdMeal: this.editVegColdMeal.trim(),
        nonVegHotMeal: this.editNonVegHotMeal.trim(),
        nonVegColdMeal: this.editNonVegColdMeal.trim(),
        specialEvents: '',
      })
      .subscribe({
        next: () => {
          this.isSavingEdit = false;
          this.closeEdit();
          this.loadMenus({ silent: true });
        },
        error: (err) => {
          this.isSavingEdit = false;
          this.errorMessage =
            err?.error?.message ||
            err?.error?.Message ||
            'Unable to save meal menu changes.';
        },
      });
  }

  private loadMenus(options: { silent?: boolean } = {}): void {
    if (!options.silent) {
      this.isInitialLoading = true;
    }
    this.errorMessage = null;

    this.mealMenuAdminService.listMenus().subscribe({
      next: (response) => {
        this.mealList = (response.menus ?? [])
          .filter((item) => this.isWeekday(item.day))
          .map((item) => this.mapToRow(item));
        this.applySearch();
        this.isInitialLoading = false;
      },
      error: (err) => {
        this.mealList = [];
        this.filteredList = [];
        this.isInitialLoading = false;
        this.errorMessage =
          err?.error?.message ||
          err?.error?.Message ||
          'Unable to load meal menu. Please try again.';
      },
    });
  }

  private mapToRow(item: MealMenuItem): MealMenuListRow {
    return {
      id: String(item.id ?? item.day),
      day: item.day?.trim() || '—',
      vegHotMeal: item.vegHotMeal?.trim() || '—',
      vegColdMeal: item.vegColdMeal?.trim() || '—',
      nonVegHotMeal: item.nonVegHotMeal?.trim() || item.hotMeal?.trim() || '—',
      nonVegColdMeal: item.nonVegColdMeal?.trim() || item.coldMeal?.trim() || '—',
    };
  }

  private resetBulkUploadState(): void {
    this.isBulkUploading = false;
    this.bulkSelectedFile = null;
    this.bulkSelectedFileName = '';
    this.bulkErrorMessage = null;
    this.bulkSuccessMessage = null;
    this.bulkRowErrors = [];
    if (this.bulkFileInput?.nativeElement) {
      this.bulkFileInput.nativeElement.value = '';
    }
  }

  private isSupportedBulkFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return lower.endsWith('.csv') || lower.endsWith('.xlsx') || lower.endsWith('.xls');
  }

  private isExcelFile(fileName: string): boolean {
    const lower = fileName.toLowerCase();
    return lower.endsWith('.xlsx') || lower.endsWith('.xls');
  }

  private parseBulkFile(file: File, result: string | ArrayBuffer | null): MealMenuBulkUpdateItem[] {
    const rows = this.isExcelFile(file.name)
      ? this.parseExcelRows(result)
      : this.parseCsvRows(String(result ?? ''));
    return this.parseBulkRows(rows);
  }

  private parseBulkRows(rows: string[][]): MealMenuBulkUpdateItem[] {
    if (rows.length < 2) {
      return [];
    }

    const headers = rows[0].map((header) => this.normalizeHeader(header));
    const hasDate = headers.includes('date');
    const hasDay = headers.includes('day');
    if (!hasDate && !hasDay) {
      throw new Error('Invalid template. Missing required column: Date (MM/DD/YYYY)');
    }

    const menus: MealMenuBulkUpdateItem[] = [];
    for (let i = 1; i < rows.length; i++) {
      const cells = rows[i];
      if (cells.every((cell) => !String(cell ?? '').trim())) {
        continue;
      }

      const get = (key: string): string => {
        const index = headers.indexOf(key);
        return index >= 0 ? String(cells[index] ?? '').trim() : '';
      };

      let day = '';
      const dateValue = get('date');
      // Skip the template format hint row (Date cell = "MM/DD/YYYY").
      if (/^mm\/dd\/yyyy$/i.test(dateValue)) {
        continue;
      }
      if (dateValue) {
        const weekday = this.weekdayFromDateValue(dateValue);
        if (!weekday) {
          throw new Error(
            `Row ${i + 1}: Invalid Date "${dateValue}". Use MM/DD/YYYY (e.g. 09/27/2026).`
          );
        }
        day = weekday;
      } else {
        day = get('day');
      }

      if (!day) {
        throw new Error(`Row ${i + 1}: Date (or Day) is required.`);
      }

      if (!this.isWeekday(day)) {
        throw new Error(
          `Row ${i + 1}: "${day}" is not allowed. Centers are closed Saturday and Sunday — use Monday–Friday only.`
        );
      }

      const hasVegHot = headers.includes('veghotmeal');
      const hasVegCold = headers.includes('vegcoldmeal');
      const hasNonVegHot = headers.includes('nonveghotmeal') || headers.includes('hotmeal');
      const hasNonVegCold = headers.includes('nonvegcoldmeal') || headers.includes('coldmeal');

      const nonVegHot = get('nonveghotmeal') || get('hotmeal');
      const nonVegCold = get('nonvegcoldmeal') || get('coldmeal');

      const item: MealMenuBulkUpdateItem = { day, specialEvents: '' };
      if (hasVegHot) {
        item.vegHotMeal = get('veghotmeal');
      }
      if (hasVegCold) {
        item.vegColdMeal = get('vegcoldmeal');
      }
      if (hasNonVegHot) {
        item.nonVegHotMeal = nonVegHot;
        item.hotMeal = nonVegHot;
      }
      if (hasNonVegCold) {
        item.nonVegColdMeal = nonVegCold;
        item.coldMeal = nonVegCold;
      }
      menus.push(item);
    }

    return menus;
  }

  private isWeekday(day: string | null | undefined): boolean {
    return this.weekdayNames.has((day ?? '').trim().toLowerCase());
  }

  /** Accept MM/DD/YYYY or YYYY-MM-DD (also Excel date strings) and return weekday name. */
  private weekdayFromDateValue(raw: string): string | null {
    const value = raw.trim();
    if (!value) {
      return null;
    }

    let parsed: Date | null = null;
    const ymd = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(value);
    const mdy = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(value);
    if (ymd) {
      parsed = new Date(Number(ymd[1]), Number(ymd[2]) - 1, Number(ymd[3]));
    } else if (mdy) {
      parsed = new Date(Number(mdy[3]), Number(mdy[1]) - 1, Number(mdy[2]));
    } else {
      const fallback = new Date(value);
      if (!Number.isNaN(fallback.getTime())) {
        parsed = fallback;
      }
    }

    if (!parsed || Number.isNaN(parsed.getTime())) {
      return null;
    }

    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    return names[parsed.getDay()] ?? null;
  }

  /** Format a date as MM/DD/YYYY for template/export. */
  private formatMdY(date: Date): string {
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    const y = date.getFullYear();
    return `${m}/${d}/${y}`;
  }

  /** Build a sample upcoming date for a weekday label (for template/export). */
  private sampleDateForWeekday(dayLabel: string, from: Date, offsetHint: number): string {
    const names = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const target = names.findIndex((n) => n.toLowerCase() === dayLabel.trim().toLowerCase());
    const base = new Date(from.getFullYear(), from.getMonth(), from.getDate() + offsetHint);
    if (target < 0) {
      return this.formatMdY(base);
    }
    const cursor = new Date(from.getFullYear(), from.getMonth(), from.getDate());
    for (let i = 0; i < 14; i++) {
      if (cursor.getDay() === target) {
        return this.formatMdY(cursor);
      }
      cursor.setDate(cursor.getDate() + 1);
    }
    return this.formatMdY(base);
  }

  private parseExcelRows(result: string | ArrayBuffer | null): string[][] {
    if (!result) {
      return [];
    }

    const data =
      result instanceof ArrayBuffer
        ? new Uint8Array(result)
        : new TextEncoder().encode(String(result));
    const workbook = XLSX.read(data, { type: 'array', cellDates: true });
    const firstSheetName = workbook.SheetNames[0];
    if (!firstSheetName) {
      return [];
    }

    const sheet = workbook.Sheets[firstSheetName];
    const matrix = XLSX.utils.sheet_to_json<(string | number | boolean | Date | null)[]>(sheet, {
      header: 1,
      defval: '',
      raw: false,
    });

    return matrix.map((row) => (row ?? []).map((cell) => String(cell ?? '').trim()));
  }

  private normalizeHeader(header: string): string {
    return header.trim().toLowerCase().replace(/[\s_-]+/g, '');
  }

  private parseCsvRows(text: string): string[][] {
    const rows: string[][] = [];
    let current: string[] = [];
    let cell = '';
    let inQuotes = false;

    const pushCell = (): void => {
      current.push(cell);
      cell = '';
    };
    const pushRow = (): void => {
      pushCell();
      rows.push(current);
      current = [];
    };

    const input = text.replace(/^\uFEFF/, '');
    for (let i = 0; i < input.length; i++) {
      const char = input[i];
      const next = input[i + 1];

      if (inQuotes) {
        if (char === '"' && next === '"') {
          cell += '"';
          i += 1;
        } else if (char === '"') {
          inQuotes = false;
        } else {
          cell += char;
        }
        continue;
      }

      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        pushCell();
      } else if (char === '\n') {
        pushRow();
      } else if (char === '\r') {
        if (next === '\n') {
          i += 1;
        }
        pushRow();
      } else {
        cell += char;
      }
    }

    if (cell.length > 0 || current.length > 0) {
      pushRow();
    }

    return rows;
  }
}
